import { useState } from "react";
import { Scrollama, Step } from "react-scrollama";
import ReportingProblemViz from "./ReportingProblemViz";
import TextBox from "../TextBox";
import {
  SectionContainer,
  ScrollingContainer,
  StepContainer,
  SectionSubheader,
} from "../SharedUI";

import styled from "@emotion/styled";

const ReportingStepContainer = styled(StepContainer)`
  min-height: 10rem;
`;

const textPalette = {
  "not-reported": "#efefef",
  "both-reported-aligned": "#077b3fff",
  "both-reported-misaligned": "#67bc81ff",
  "importer-only": "#b6b108ff",
  "exporter-only": "#45c2efff",
};

export default function ReportingProblem() {
  const [currentStep, setCurrentStep] = useState("ideal");

  const onStepEnter = ({ data }: { data: string }) => {
    setCurrentStep(data);
  };

  return (
    <SectionContainer>
      <ReportingProblemViz currentStep={currentStep} />
      <ScrollingContainer>
        <Scrollama onStepEnter={onStepEnter}>
          <Step data={"ideal"}>
            <ReportingStepContainer>
              <TextBox isActive={currentStep === "ideal"}>
                <SectionSubheader>
                  1.1 How Significant is the Reporting Problem?
                </SectionSubheader>
                <p>
                  Let's plot each country-country trading pair on a grid. In an
                  ideal case, all countries report trade with all partners, and
                  exporter and importer values align within a reasonable margin,
                  say{" "}
                  <span style={{ color: textPalette["both-reported-aligned"] }}>
                    25%
                  </span>
                  . If this were true, every cell would be green, indicating
                  complete reporting and aligned export and import values across
                  all country pairs.
                </p>
              </TextBox>
            </ReportingStepContainer>
          </Step>
          <Step data={"actual"}>
            <ReportingStepContainer>
              <TextBox isActive={currentStep === "actual"}>
                <p>
                  In reality, the grid is fragmented, reflecting uneven
                  reporting across countries. Let's consider 2010 global trade
                  data as a representative example.
                </p>
              </TextBox>
            </ReportingStepContainer>
          </Step>
          <Step data={"reality-both-reported-aligned"}>
            <ReportingStepContainer>
              <TextBox
                isActive={currentStep === "reality-both-reported-aligned"}
              >
                <p>
                  <span
                    style={{
                      fontWeight: 600,
                      color: textPalette["both-reported-aligned"],
                    }}
                  >
                    22% of country pairs reported trade values that aligned,
                  </span>{" "}
                  with a discrepancy of less than 25% between the exporter's and
                  importer's values.
                </p>
              </TextBox>
            </ReportingStepContainer>
          </Step>
          <Step data={"reality-add-both-misaligned"}>
            <ReportingStepContainer>
              <TextBox isActive={currentStep === "reality-add-both-misaligned"}>
                <p>
                  <span
                    style={{
                      fontWeight: 600,
                      color: textPalette["both-reported-misaligned"],
                    }}
                  >
                    23% of country pairs both reported, but with major
                    discrepancies.
                  </span>{" "}
                  Each side recorded the same trade relationship, but their
                  values differed by more than 25%.
                </p>
              </TextBox>
            </ReportingStepContainer>
          </Step>
          <Step data={"reality-add-importer-only"}>
            <ReportingStepContainer>
              <TextBox isActive={currentStep === "reality-add-importer-only"}>
                <p>
                  <span>
                    In the remaining 55% of pairs, at least one country did not
                    report any trade.
                  </span>{" "}
                  <span
                    style={{
                      fontWeight: 600,
                      color: textPalette["importer-only"],
                    }}
                  >
                    11% were reported only by importers...
                  </span>
                </p>
              </TextBox>
            </ReportingStepContainer>
          </Step>
          <Step data={"reality-add-exporter-only"}>
            <ReportingStepContainer>
              <TextBox isActive={currentStep === "reality-add-exporter-only"}>
                <p>
                  ...and{" "}
                  <span
                    style={{
                      fontWeight: 600,
                      color: textPalette["exporter-only"],
                    }}
                  >
                    10% only by exporters.
                  </span>{" "}
                  For the remaining 34% of country pairs, neither country
                  reported trade data.
                </p>
              </TextBox>
            </ReportingStepContainer>
          </Step>
          <Step data={"reality"}>
            <ReportingStepContainer>
              <TextBox isActive={currentStep === "reality"}>
                <p>
                  The resulting grid diverges sharply from the ideal. Much of
                  global trade data is missing, unevenly reported, or
                  inconsistent across countries.
                </p>
                <p>
                  Before global trade data can reliably inform policy or
                  economic analysis, these gaps and inconsistencies must be
                  addressed.
                </p>
              </TextBox>
            </ReportingStepContainer>
          </Step>
        </Scrollama>
        <div style={{ height: "30vh" }}></div>
      </ScrollingContainer>
    </SectionContainer>
  );
}
